import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, getDocs, orderBy, Timestamp, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import styles from '../styles/LoginMapPage.module.css'; // Create this new CSS file

// Import React Leaflet components
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css'; // Import Leaflet's CSS
import L from 'leaflet'; // Import Leaflet library itself to fix icon issue

// Fix for default marker icon issue with Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});


const getDateString = (date) => {
    return new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
};

const LoginMapPage = () => {
    const { userRole } = useAuth();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedDate, setSelectedDate] = useState(getDateString(new Date()));

    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => { isMounted.current = false; };
    }, []);

    // Fetch logs based on the selected date
    useEffect(() => {
        if (!selectedDate) return;

        const fetchLogsForDate = async () => {
            if (!isMounted.current) return;
            setLoading(true);
            setError('');
            try {
                const startOfDay = new Date(selectedDate + 'T00:00:00');
                const endOfDay = new Date(selectedDate + 'T23:59:59');

                const logsCol = collection(db, 'loginHistory');
                const logsQuery = query(logsCol, 
                    where('timestamp', '>=', Timestamp.fromDate(startOfDay)),
                    where('timestamp', '<=', Timestamp.fromDate(endOfDay)),
                    orderBy('timestamp', 'desc')
                );

                const logsSnapshot = await getDocs(logsQuery);
                if (!isMounted.current) return;

                const logsData = logsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    timestamp: doc.data().timestamp.toDate(),
                }));
                setLogs(logsData);

            } catch (err) {
                console.error(`Error fetching logs for ${selectedDate}:`, err);
                if (isMounted.current) {
                    if (err.code === 'failed-precondition') {
                         setError("A database index is required for this query. Please check the browser console for a link to create it.");
                    } else {
                         setError("Failed to fetch login logs for the selected date.");
                    }
                }
            } finally {
                if (isMounted.current) setLoading(false);
            }
        };

        fetchLogsForDate();
    }, [selectedDate]);

    // Filter out logs that don't have valid coordinates
    const logsWithLocation = useMemo(() => {
        return logs.filter(log => log.location && typeof log.location.lat === 'number' && typeof log.location.lon === 'number');
    }, [logs]);
    
    const todayForDateInput = useMemo(() => getDateString(new Date()), []);

    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                <h2 className={styles.pageTitle}>Staff Login Map</h2>
                <div className={styles.filterGroup}>
                    <label htmlFor="log-date-filter" className={styles.dateFilterLabel}>Show Logins For:</label>
                    <input
                        type="date"
                        id="log-date-filter"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        className={styles.dateInput}
                        max={todayForDateInput}
                    />
                </div>
            </div>

            {error && <div className={styles.errorMessage}>{error}</div>}

            <div className={styles.mapWrapper}>
                {loading ? (
                    <div className={styles.loadingIndicator}>Loading Map & Login Data...</div>
                ) : (
                    <MapContainer center={[25.2048, 55.2708]} zoom={4} style={{ height: '100%', width: '100%' }}>
                        <TileLayer
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        {logsWithLocation.map(log => (
                            <Marker key={log.id} position={[log.location.lat, log.location.lon]}>
                                <Popup>
                                    <strong>{log.userName}</strong> ({log.userRole})
                                    <br />
                                    Logged in at: {log.timestamp.toLocaleTimeString()}
                                    <br />
                                    Location: {log.location.city}, {log.location.country}
                                    <br />
                                    IP: {log.ipAddress}
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                )}
                 {!loading && logsWithLocation.length === 0 && (
                    <div className={styles.noDataMessage}>No logins with location data found for {selectedDate}.</div>
                )}
            </div>
        </div>
    );
};

export default LoginMapPage;